/**
 * @file src/utils/security.js
 * @description Security utilities for input sanitization and redirect validation.
 */

import DOMPurify from 'dompurify'

/**
 * Whitelist of trusted hostnames for payment redirects.
 * @type {string[]}
 */
const ALLOWED_REDIRECT_HOSTS = [
  'checkout.stripe.com',
  'www.paypal.com',
  'www.sandbox.paypal.com',
  'marathon-photos.com',
  'www.marathon-photos.com',
  'sportograf.com',
  'www.sportograf.com',
]

/**
 * Validates that a URL is safe for redirect (HTTPS + whitelisted host).
 * @param {string} url - The URL to validate
 * @returns {boolean} True if the URL is allowed
 */
export function isAllowedRedirect(url) {
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:') return false
    return ALLOWED_REDIRECT_HOSTS.some(
      host => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`)
    )
  } catch {
    return false
  }
}

/**
 * Escapes HTML entities to prevent XSS when interpolating into HTML.
 * @param {string} str - The string to escape
 * @returns {string} The escaped string
 */
export function escapeHtml(str) {
  if (typeof str !== 'string') return ''
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Sanitizes HTML using DOMPurify, keeping safe formatting tags.
 * @param {string} html - The HTML string to sanitize
 * @returns {string} The sanitized HTML
 */
export function sanitizeHtml(html) {
  if (typeof html !== 'string') return ''
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'b', 'i', 'u'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
  })
}

/**
 * Sanitizes terms and conditions HTML using DOMPurify.
 * Allows additional tags and attributes needed by legal documents
 * (img, start attribute for ordered lists, type for sub-lists).
 * @param {string} html - The HTML string to sanitize
 * @returns {string} The sanitized HTML
 */
export function sanitizeTermsHtml(html) {
  if (typeof html !== 'string') return ''
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'b', 'i', 'u', 'img'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'start', 'type',
      'src', 'alt', 'data-logo'],
  })
}

/**
 * Strips dangerous patterns from CSS strings (script tags, javascript: URLs,
 * expression(), @import, -moz-binding).
 * @param {string} css - The CSS string to sanitize
 * @returns {string} The sanitized CSS
 */
export function sanitizeCss(css) {
  if (typeof css !== 'string') return ''
  return css
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/javascript\s*:/gi, '')
    .replace(/expression\s*\(/gi, '')
    .replace(/@import\b/gi, '')
    .replace(/-moz-binding\s*:/gi, '')
}
